import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import {
    Card,
    Block,
    FinalTable,
    FinalTableColumn,
    useFinalTable,
    useFinalTableData,
    Icon,
    Modal,
    Space,
    Button,
    FinalTableFiltersDef,
    FilterDef,
} from '@mc/ui-v4';
import {
    LayerAttributeVO,
    CatalogItemVO,
    AkceApi,
    LayerDetailVO,
    UserLayersApi,
    EnumTypLayerAttr,
    EnumInterpretace,
    DataGridApi,
    DataGridRowVO,
    DataGridFilterVO,
    RightsApi,
    UserRightsVO,
    BatchUpdateParamsVO,
} from '../../api';
import {DataRequestType, DataRequestVO, DataResponseVO, FieldValuesFilterType, getDataFilterExportDownloadUrl} from '@mc/data-filter-react';
import {useLayerAttributes} from '../../hooks/useLayerAttributes';
import {useMapioContext} from '../../mapioContext';
import {useCatalogLayers} from '../../hooks/useCatalogLayers';
import {UseQueryResult, useQueryClient, QueryClient, useQuery} from 'react-query';
import {FeaturePointer, LayerId, EditorEventType, FeatureId, FeatureFilterType, GroupFeaturePointer, ActionTypes} from '@mc/react-mapio';
import {buildCiselnikFilterItems, findLayer, findNestedLabelById, hasActiveChildren, truncate} from '../../helpers/DataMutators';
import {htmlToPlainText, sanitizeUserHtml} from '../../helpers/sanitizeHtml';
import {getExportFormatItems} from '../../enums/ExportFormats';
import {useAppConfig} from '@mc/react-app-base';
import {AttributeTableTitle} from './AttributeTable/AttributeTableTitle';
import {AttributeTableToolbar} from './AttributeTable/AttributeTableToolbar';
import {AttributeTableRowActions} from './AttributeTable/AttributeTableRowActions';
import {ApiCallWaitingModal} from '../modals/ApiCallWaitingModal';
import {BulkEditModal} from '../forms/BulkEditModal';
import {actionApiCall} from '@mc/react-app-v4';
import {SERVER_CONTEXT_PATH} from '../../const';
import {ATTRIBUTE_TABLE_FT_KEY} from '../../const/FinalTableKeys';
import {ATTRIBUTE_TABLE_SETTINGS_KEY} from '../../const/SettingsKeys';
import {CACHE_KEY_USER_LAYER_OPRAVNENI} from '../../queryKeys';
import {useLayersConfig} from '../../hooks/useLayersConfig';
import './AttributeTable.scss';
import {
    isUserDbLayer,
    resolveMoveLayerId,
    filterLayersByKonektor,
    isParentOfUserDbLayers,
    hasParentLayer,
    getParentLayerId,
} from '../../helpers/LayerConfigUtils';
import {ACCESSIBILITY_ITEMS, parseAccessibilityValue} from '../../assets/accessibility/accessibilityCodelist';
import {formatDate} from '../../helpers/Formatter';
import {sameFeatureIdSet} from '../../helpers/FeatureSelectionUtils';
import {mcCheck, mcClose} from '@mc/react-ui-icons';
import {CACHE_FEATURES_IDS} from '../../hooks/useFeatureIds';
import {useTableResize} from '../../hooks/useTableResize';
import {useDiscriminatorData} from '../../hooks/useDiscriminatorData';
import {GRID_INTERNAL_ID_FIELD, gridFieldName} from '../../helpers/DataGridFields';

/** Hlavička i popisek filtru sloupce s interním ID – shodné s {@code MapioDataGridService.INTERNAL_ID_CAPTION}. */
const INTERNAL_ID_LABEL = 'Interní ID';

/**
 * Položky výběrového filtru atributu typu Přístupnost pro vozíčkáře. Hodnotou je kód přístupnosti,
 * který server hledá v surové hodnotě atributu (seznam kódů oddělený středníkem).
 */
const ACCESSIBILITY_FILTER_ITEMS = ACCESSIBILITY_ITEMS.map(item => ({id: item.kod, name: item.nazev}));

/** Na kolik znaků zkrátíme čistý text formátovaného atributu v buňce tabulky (celý je v tooltipu). */
const FORMATTED_TEXT_CELL_MAX_LENGTH = 150;

export type UserAction = {
    label?: string;
    type?: EditorEventType;
    actionId?: number | ActionTypes | null;
    featureId?: FeatureId;
    layerId?: LayerId;
    akceId?: number | null;
};

type HandleUpdateFn = (
    feature: LayerDetailVO | DataGridRowVO,
    layerAttributes: LayerAttributeVO[],
    initialValues: Record<string, unknown>,
    update: boolean,
    actionId?: number | null,
    invalidate?: (featureId?: FeatureId) => void,
) => void;

type Props = {
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
    handleUpdate: HandleUpdateFn;
    handleDelete: (featurePointers: FeaturePointer[], invalidate?: () => void) => void;
    handleMove: (featurePointers: FeaturePointer[]) => void;
    selectedDetailFeature?: GroupFeaturePointer[];
    extentFeatures?: FeatureId[];
    isExtentChecked?: boolean;
    setIsExtentChecked?: React.Dispatch<React.SetStateAction<boolean>>;
    isProvazatChecked?: boolean;
    setIsProvazatChecked?: React.Dispatch<React.SetStateAction<boolean>>;
    setActiveFilterLayerId?: React.Dispatch<React.SetStateAction<LayerId>>;
};

export const AttributeTableFinal: React.FC<Props> = ({
    selectedDetailFeature,
    setIsOpen,
    handleUpdate,
    handleDelete,
    handleMove,
    extentFeatures,
    isExtentChecked,
    setIsExtentChecked,
    isProvazatChecked,
    setIsProvazatChecked,
    setActiveFilterLayerId,
}) => {
    const mapioContext = useMapioContext();
    const {setSelectedLayerId, setAttributeTableDiscriminatedChildId} = mapioContext;
    const {mapRef, topicId, context, selectedLayerId, activeLayers, attributeTableDiscriminatedChildId} = mapioContext.value;
    //@ts-ignore
    const {serverPublic} = useAppConfig();
    const queryClient: QueryClient = useQueryClient();

    const {tableHeight, handleMouseDown} = useTableResize('.att_table_container');

    const layerAttributesQuery = useLayerAttributes(selectedLayerId);
    const layersConfigQuery = useLayersConfig();

    const userDbLayers: LayerId[] = useMemo(() => {
        if (!layersConfigQuery.data || !activeLayers) return [];
        return filterLayersByKonektor(layersConfigQuery.data, activeLayers.map(Number), 'user_db');
    }, [layersConfigQuery.data, activeLayers]);

    const discriminatorData = useDiscriminatorData(selectedLayerId, attributeTableDiscriminatedChildId, layerAttributesQuery, layersConfigQuery, userDbLayers);

    const formats = useMemo(() => getExportFormatItems(serverPublic), [serverPublic]);
    const catalogLayersQuery: UseQueryResult<CatalogItemVO[]> = useCatalogLayers(topicId);
    // Příznak: checkboxy byly vymazány kvůli kliknutí na jinou vrstvu — handleMove nesmí odvybrat mapu
    const suppressHandleMoveRef = useRef(false);
    // Předchozí hodnota provázání — abychom rozpoznali přechod vypnuto→zapnuto a přeskočili
    // v tu chvíli přenos tabulka→mapa (jinak si oba efekty přepisují starý stav a rozkmitají se).
    const prevProvazatRef = useRef(isProvazatChecked);

    // Aktualizace checkboxů + informování rodiče o filterLayerId
    const relevantLayerIds = useMemo(() => {
        const ids = new Set<LayerId>([selectedLayerId]);
        if (discriminatorData.discriminatorAttribute && layersConfigQuery.data) {
            userDbLayers.filter(childId => getParentLayerId(layersConfigQuery.data, childId) === selectedLayerId).forEach(childId => ids.add(childId));
        }
        if (discriminatorData.discriminatorAttribute?.layerId) {
            ids.add(discriminatorData.discriminatorAttribute.layerId);
        }
        return ids;
    }, [selectedLayerId, discriminatorData.discriminatorAttribute, layersConfigQuery.data, userDbLayers]);

    useEffect(() => {
        const filterLayerId =
            discriminatorData.discriminatorAttribute?.layerId && discriminatorData.matchingCiselnik
                ? discriminatorData.discriminatorAttribute.layerId
                : selectedLayerId;
        setActiveFilterLayerId?.(filterLayerId);

        // Pokud je provazování vypnuté, tabulka si spravuje výběr sama — mapa nesmí nic ovlivnit
        if (!isProvazatChecked) return;

        const featuresForThisLayer = selectedDetailFeature?.filter(f => relevantLayerIds.has(f.layerId)) ?? [];
        const clickedDifferentLayer = (selectedDetailFeature?.length ?? 0) > 0 && featuresForThisLayer.length === 0;

        const hasCheckedIds = (attrFinalTable?.checkedIds?.length ?? 0) > 0;

        if (clickedDifferentLayer) {
            // Klik na jinou vrstvu — obsah tabulky zůstane, ale zaškrtnuté řádky se odznačí.
            // suppressHandleMoveRef zabrání tomu, aby handleMove([]) odvybral objekt na mapě.
            // Guard: nulujeme jen když opravdu něco zaškrtnuto je, jinak zbytečný re-render.
            if (attrFinalTable && hasCheckedIds) {
                suppressHandleMoveRef.current = true;
                attrFinalTable.setCheckedIds([]);
            }
        } else if (selectedDetailFeature?.length === 0) {
            if (attrFinalTable && hasCheckedIds) {
                attrFinalTable.setCheckedIds([]);
            }
        } else if (featuresForThisLayer.length > 0) {
            const targetIds = featuresForThisLayer.map(f => Number(f.featureId));
            // Guard: přenášíme jen při reálné změně, ať nevyvoláme zbytečný cyklus efektů.
            if (attrFinalTable && !sameFeatureIdSet(attrFinalTable.checkedIds, targetIds)) {
                attrFinalTable.setCheckedIds(targetIds);
            }
        }
    }, [isProvazatChecked, selectedDetailFeature, discriminatorData, selectedLayerId, relevantLayerIds]);

    // ------------------- Layer selection logic (same as AttributeTable) -------------------
    useEffect(() => {
        if (catalogLayersQuery.isLoading || layersConfigQuery.isLoading) return;
        if (!catalogLayersQuery.data || !userDbLayers.length) {
            setIsOpen(false);
            return;
        }

        const catalogData = catalogLayersQuery.data;
        const isCurrentLayerInCatalog = (id: LayerId) => (catalogData ? findLayer(catalogData, id) : false);
        const checkHasActiveChildren = (id: LayerId) => (catalogData ? hasActiveChildren(catalogData, id, userDbLayers) : false);

        if (userDbLayers.length && !selectedLayerId) {
            setSelectedLayerId(userDbLayers[0]);
            return;
        }

        // Pokud je vybraná vrstva child (má parent), přesměrujeme na parent a nastavíme discriminatedChildId
        if (selectedLayerId && hasParentLayer(layersConfigQuery.data, selectedLayerId)) {
            const pId = getParentLayerId(layersConfigQuery.data, selectedLayerId);
            if (pId) {
                setAttributeTableDiscriminatedChildId(selectedLayerId);
                setSelectedLayerId(pId);
            }
            return;
        }

        if (
            selectedLayerId &&
            !isUserDbLayer(layersConfigQuery.data, selectedLayerId) &&
            !isParentOfUserDbLayers(layersConfigQuery.data, userDbLayers, selectedLayerId)
        ) {
            if (userDbLayers.length > 0) setSelectedLayerId(userDbLayers[0]);
            return;
        }

        if (userDbLayers.length) {
            const isValidLayer = userDbLayers.includes(selectedLayerId) || isParentOfUserDbLayers(layersConfigQuery.data, userDbLayers, selectedLayerId);
            if (!isValidLayer || !checkHasActiveChildren(selectedLayerId)) {
                setSelectedLayerId(userDbLayers[0]);
                return;
            }
        }

        if (selectedLayerId && isCurrentLayerInCatalog(selectedLayerId) && checkHasActiveChildren(selectedLayerId)) {
            if (isExtentChecked) {
                mapRef.current.setLayerFeaturesFilter(FeatureFilterType.SHOW_EXTENT, discriminatorData.discriminatorAttribute?.layerId ?? selectedLayerId);
            }
        }
    }, [selectedLayerId, userDbLayers, catalogLayersQuery.data, isExtentChecked, mapRef, layersConfigQuery.data, layerAttributesQuery.data, discriminatorData]);

    // ------------------- Dynamic columns and filters from layer attributes -------------------

    const columns = useMemo<FinalTableColumn<DataGridRowVO>[]>(() => {
        if (!layerAttributesQuery.data) return [];

        const attrCols: FinalTableColumn<DataGridRowVO>[] = layerAttributesQuery.data.map((col: LayerAttributeVO) => {
            // Pole v datech gridu se u atributu pojmenovaného "id" liší od jeho názvu – viz gridFieldName
            const field = gridFieldName(col.nazev);
            const base = {
                accessor: field,
                header: col.popis,
                sortable: true,
                groupable: true,
            };
            if (col.typ === EnumTypLayerAttr.NUMBER) {
                return {
                    ...base,
                    type: 'text',
                    cell: ({row}) => {
                        const value = row[field];
                        return value == null ? (row.delka ?? row.plocha) : Number(value).toLocaleString();
                    },
                };
            }
            if (col.typ === EnumTypLayerAttr.ENUM) {
                // Vícehodnotový číselník je vazba 1:N – server ho do dotazu gridu nezahrnuje (hodnoty doplňuje
                // až do řádků), takže na něm nelze serverově řadit, seskupovat ani filtrovat dle hodnot sloupce.
                const isMultiCiselnik = col.interpretace === EnumInterpretace.MULTI_CISELNIK;
                return {
                    ...base,
                    ...(isMultiCiselnik && {sortable: false, groupable: false, filterable: false}),
                    type: 'text',
                    cell: ({row}) => {
                        const value = row[field];
                        // číselník může přijít jako pole názvů (grid), pole hodnot (LayerAttributeEnumVO[])
                        // nebo jako samotný název (jednohodnotový číselník v gridu)
                        if (Array.isArray(value)) {
                            return value
                                .map(v => (typeof v === 'string' ? v : v?.nazev))
                                .filter(Boolean)
                                .join(', ');
                        }
                        return value?.nazev ?? value;
                    },
                };
            }
            if (col.typ === EnumTypLayerAttr.DATE_TIME) {
                return {
                    ...base,
                    type: 'text',
                    width: 200,
                    cell: ({row}) => formatDate(row[field]),
                    title: ({row}) => formatDate(row[field]),
                };
            }
            if (col.typ === EnumTypLayerAttr.BOOLEAN) {
                return {
                    ...base,
                    type: 'text',
                    cell: ({row}) =>
                        row[field] ? (
                            <span>
                                <Icon icon={mcCheck} color="green" /> Ano
                            </span>
                        ) : (
                            <span>
                                <Icon icon={mcClose} color="red" /> Ne
                            </span>
                        ),
                };
            }
            if (col.typ === EnumTypLayerAttr.ACCESSIBILITY) {
                return {
                    ...base,
                    filterable: false,
                    type: 'text',
                    width: 380,
                    cell: ({row}) => {
                        const activeCodes = parseAccessibilityValue(row[field] as string);
                        // Vykreslíme VŠECHNY ikony přístupnosti; ty, které objekt nemá, zobrazíme zašedle
                        // (neaktivní) – stejně jako v detailu objektu.
                        return (
                            <div className="accessibility-cell">
                                {ACCESSIBILITY_ITEMS.map(item => {
                                    const inactive = !activeCodes.includes(item.kod);
                                    return (
                                        <img
                                            key={item.kod}
                                            src={item.icon}
                                            alt={item.nazev}
                                            title={item.nazev}
                                            className={`accessibility-cell__icon${inactive ? ' accessibility-cell__icon--inactive' : ''}`}
                                        />
                                    );
                                })}
                            </div>
                        );
                    },
                };
            }
            if (col.interpretace === EnumInterpretace.FORMATOVANYTEXT) {
                // Formátovaný text (WYSIWYG) do jednoho řádku tabulky vykreslit nelze – v buňce ukážeme
                // zkrácený čistý text bez HTML značek, celý formátovaný obsah pak v tooltipu po najetí myší
                // (title vracející ReactNode obalí FinalTable komponentou Tooltip – viz @mc/ui-v4 BodyCell).
                return {
                    ...base,
                    type: 'text',
                    cell: ({row}) => truncate(htmlToPlainText(row[field] as string), FORMATTED_TEXT_CELL_MAX_LENGTH),
                    title: ({row}) => {
                        const html = row[field] as string;
                        if (!html) return undefined;
                        return <div className="formatted-text-tooltip" dangerouslySetInnerHTML={{__html: sanitizeUserHtml(html)}} />;
                    },
                };
            }
            return {
                ...base,
                type: 'text',
                // Bez vlastního cell by FinalTable hodnotu hledala přes getDataRowColumnValue, která accessor
                // rozděluje podle teček jako vnořenou cestu (@mc/ui-v4) – atributy s tečkou v názvu (např. "ev. cislo")
                // by se tak nikdy nezobrazily, přestože je řádek obsahuje pod plochým klíčem.
                cell: ({row}) => row[field] as React.ReactNode,
            };
        });

        // Interní ID objektu v Mapiu. Zobrazujeme ho jako samostatný sloupec, protože data mohou mít
        // vlastní (zákaznické) ID jako běžný atribut – ať je z hlavičky poznat, které ID je které.
        // Filtrování dle hodnot vypínáme – výčet hodnot technického ID nemá pro uživatele smysl,
        // omezení rozsahem se dělá filtrem od-do v panelu filtrů (viz filtersDef).
        const internalIdCol: FinalTableColumn<DataGridRowVO> = {
            accessor: GRID_INTERNAL_ID_FIELD,
            header: INTERNAL_ID_LABEL,
            headerTitle: 'Interní ID objektu',
            type: 'text',
            width: 110,
            sortable: true,
            groupable: false,
            filterable: false,
        };

        // Action buttons column
        const actionCol: FinalTableColumn<DataGridRowVO> = {
            type: 'action',
            cell: ({row, rowIndex}: {row: DataGridRowVO; rowIndex: number}) => (
                <AttributeTableRowActions
                    row={row}
                    rowIndex={rowIndex}
                    handleUpdateClick={handleUpdateClick}
                    handleDeleteClick={handleDeleteClick}
                    layerId={selectedLayerId}
                />
            ),
        };

        return [internalIdCol, ...attrCols, actionCol];
    }, [layerAttributesQuery.data, selectedLayerId]);

    // ------------------- FinalTable hook -------------------
    // settingsKey je per-layer, aby si každá vrstva pamatovala své vlastní nastavení
    const settingsKey = `${ATTRIBUTE_TABLE_SETTINGS_KEY}-${selectedLayerId ?? 'none'}`;

    // Klíč identifikuje kombinaci (rodičovská vrstva, diskriminované dítě) – při změně se tabulka reinicializuje
    const ftKey = `${ATTRIBUTE_TABLE_FT_KEY}-${selectedLayerId ?? 'none'}-${attributeTableDiscriminatedChildId ?? 'none'}`;

    const attrFinalTableData = useFinalTableData(ftKey);

    // Snapshot diskriminátoru pro referenci v efektu níže
    const closureDiscAttrNazev = discriminatorData.discriminatorAttribute?.nazev;
    const closureMatchCiselnikId = discriminatorData.matchingCiselnik?.nazev;

    // Refs pro extent filtr – zajistí, že api callback vždy čte aktuální hodnoty
    // bez nutnosti reinicializace useFinalTable
    const isExtentCheckedRef = useRef(isExtentChecked);
    isExtentCheckedRef.current = isExtentChecked;
    const extentFeaturesRef = useRef(extentFeatures);
    extentFeaturesRef.current = extentFeatures;

    // Název souboru pro export do XLS – dle názvu vrstvy a aktuálního data
    // (znaky nepovolené v názvu souboru nahradíme, příponu doplní server)
    const exportFileName = useMemo(() => {
        const layerLabel = findNestedLabelById(catalogLayersQuery?.data, selectedLayerId);
        const nazev = (layerLabel || 'atributova-tabulka').replace(/[\\/:*?"<>|]/g, '_');
        const dnes = new Date();
        const datum = `${dnes.getFullYear()}-${String(dnes.getMonth() + 1).padStart(2, '0')}-${String(dnes.getDate()).padStart(2, '0')}`;
        return `${nazev}-${datum}`;
    }, [catalogLayersQuery.data, selectedLayerId]);

    const filters = useMemo<FinalTableFiltersDef>(() => {
        if (!layerAttributesQuery.data) return {filtersDef: []};

        const filtersDef: FilterDef[] = layerAttributesQuery.data.flatMap((col: LayerAttributeVO) => {
            // Filtr musí mít stejný název jako pole v datech gridu (u atributu "id" je aliasovaný)
            const field = gridFieldName(col.nazev);
            if (col.typ === EnumTypLayerAttr.ENUM) {
                return [
                    {
                        name: field,
                        type: 'multiSelect',
                        label: col.popis,
                        multiSelectProps: {
                            // U stromového číselníku hodnoty vypíšeme v pořadí stromu a hierarchii naznačíme
                            // odsazením – stejně jako ve formuláři objektu (výběr hodnot strom neumí)
                            items: buildCiselnikFilterItems(col.ciselniky),
                        },
                    } as FilterDef,
                ];
            }
            if (col.typ === EnumTypLayerAttr.DATE_TIME) {
                return [{name: field, type: 'dateTimeRange', label: col.popis} as FilterDef];
            }
            if (col.typ === EnumTypLayerAttr.BOOLEAN) {
                return [{name: field, type: 'checkbox', checkboxProps: {defaultChecked: null, allowNullValue: true}, label: col.popis} as FilterDef];
            }
            if (col.typ === EnumTypLayerAttr.NUMBER) {
                return [{name: field, type: 'currencyDecimalRange', label: col.popis} as FilterDef];
            }
            if (col.typ === EnumTypLayerAttr.INT) {
                return [{name: field, type: 'integerRange', label: col.popis} as FilterDef];
            }
            if (col.typ === EnumTypLayerAttr.ACCESSIBILITY) {
                // Atribut je vícehodnotový (kódy přístupnosti) – vybrané kódy hledá server
                // v surové hodnotě sloupce, viz MapioDataGridService.registerAccessibilityWhereFilter
                return [{name: field, type: 'multiSelect', label: col.popis, multiSelectProps: {items: ACCESSIBILITY_FILTER_ITEMS}} as FilterDef];
            }
            // STRING, TEXT → text filter
            return [{name: field, type: 'text', label: col.popis} as FilterDef];
        });
        // Interní ID objektu není mezi atributy vrstvy, filtr od-do pro něj doplníme zvlášť
        // (pořadím odpovídá prvnímu sloupci tabulky)
        filtersDef.unshift({name: GRID_INTERNAL_ID_FIELD, type: 'integerRange', label: INTERNAL_ID_LABEL} as FilterDef);
        return {filtersDef};
    }, [layerAttributesQuery.data]);

    const attrFinalTable = useFinalTable<DataGridRowVO, DataGridFilterVO, number>(ftKey, {
        settingsKey,
        api: async request => {
            // useFinalTable funguje v "ids" módu:
            //   1. IDS request – tabulka žádá seznam všech ID řádků
            //   2. DATA request (s idsRequest) – tabulka žádá data pro viditelné řádky
            //
            // Při aktivním extent filtru vrátíme pro IDS request přímo ID ze stavu mapy
            // (map je již vrátila přes EXTENT_FEATURES event) – nepotřebujeme server.
            // DATA requesty nechme projít normálně – server vrátí data pro konkrétní IDs.
            if (request.requestType === DataRequestType.IDS && isExtentCheckedRef.current) {
                const ids = (extentFeaturesRef.current ?? []) as unknown as object[];
                return {
                    ids,
                    totalElements: ids.length,
                    totalDataElements: ids.length,
                } as DataResponseVO<DataGridRowVO>;
            }
            // Export do XLS: server o omezení výřezem mapy neví, proto mu ID z výřezu předáme.
            // Pokud si uživatel vybral konkrétní řádky, mají přednost (jsou podmnožinou výřezu).
            let exportRequest = request;
            if (request.requestType === DataRequestType.EXPORT_EXCEL && isExtentCheckedRef.current && !request.idsRequest) {
                exportRequest = {...request, idsRequest: {ids: (extentFeaturesRef.current ?? []) as unknown as object[]}};
            }
            return (await DataGridApi.findLayerDataByFilter(Number(selectedLayerId), exportRequest, context ?? undefined)).data;
        },
        columnsFilterable: true,
        checkable: true,
        // autoUseLastFilter záměrně nepoužíváme (MCW-29314): po obnovení stránky se filtr obnovil z „posledního
        // hledání“, ale seznam vybraných položek filtru jsme si hned nastavovali na výchozí (viz efekt níže),
        // takže tabulka filtrovala i podle hodnot, které uživatel v dialogu neměl zaškrtnuté. Po načtení
        // stránky proto začínáme s prázdným filtrem.
        // exportable záměrně nepoužíváme – XLS se spouští z exportního tlačítka v záhlaví panelu
        // (viz handleExportXlsx), ať uživatel nemá dvě tlačítka pro export
        filters,
        columns,
    });

    /**
     * Export do XLS standardním mechanismem FinalTable: na datový endpoint gridu se pošle požadavek
     * typu EXPORT_EXCEL (se stejným filtrem a řazením jako tabulka) a vrácený soubor se stáhne
     * standardním endpointem data-filtru. Exportují se aktuálně viditelné sloupce a zaškrtnuté
     * řádky (pokud žádné nejsou zaškrtnuté, tak vše dle filtru).
     */
    const handleExportXlsx = useCallback(async () => {
        const request = attrFinalTable.getExportDataRequest() as DataRequestVO<DataGridFilterVO>;
        request.requestType = DataRequestType.EXPORT_EXCEL;
        request.excelExportRequest = {
            columnsOrder: attrFinalTable.columnsOrder,
            visibleColumns: attrFinalTable.visibleColumns,
            fileName: exportFileName,
        };

        const checkedIds = attrFinalTable.checkedIds ?? [];
        if (checkedIds.length > 0 && checkedIds.length !== attrFinalTable.totalElements) {
            request.idsRequest = {ids: checkedIds as unknown as object[]};
        }

        const result = await Modal.showWaiting(
            // Omezení výřezem mapy řeší api funkce tabulky (viz výše)
            actionApiCall(async () => attrFinalTable.config.api(request, {hardFilter: attrFinalTable.hardFilter})),
            props => <ApiCallWaitingModal {...props} title={'Probíhá export do XLS'} />,
        );

        if (result?.exportFileId) {
            window.open(SERVER_CONTEXT_PATH + getDataFilterExportDownloadUrl(result.exportFileId), '_blank');
        }
    }, [attrFinalTable, exportFileName]);

    // Vrátí řádek tabulky podle jeho ID (přes aktuálně načtená data tabulky).
    const getRowById = useCallback(
        (id: number): DataGridRowVO | undefined => {
            const index = attrFinalTable.ids?.indexOf(id);
            return index != null && index >= 0 ? (attrFinalTable.getRowByIndex(index) as DataGridRowVO | undefined) : undefined;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [attrFinalTable.ids, attrFinalTable.getRowByIndex],
    );

    useEffect(() => {
        const wasProvazat = prevProvazatRef.current;
        prevProvazatRef.current = isProvazatChecked;

        if (!isProvazatChecked) return;

        // Přechod vypnuto→zapnuto: autoritou je mapa a výběr přenese mapa→tabulka (efekt výše).
        // Tady tabulka→mapa přeskočíme, jinak by oba efekty četly navzájem starý stav
        // (setCheckedIds je asynchronní) a rozkmitaly by výběr do nekonečna → pád Toolbaru.
        if (!wasProvazat) return;

        if (attrFinalTable?.checkedIds?.length > 0) {
            const pointers: FeaturePointer[] = attrFinalTable.checkedIds.map(id => {
                const index = attrFinalTable.ids?.indexOf(id);
                const row = index != null && index >= 0 ? attrFinalTable.getRowByIndex(index) : undefined;
                const layerId = row
                    ? resolveMoveLayerId(layersConfigQuery.data, userDbLayers, selectedLayerId, row, discriminatorData.discriminatorAttribute)
                    : selectedLayerId;
                return {layerId, featureId: id};
            });
            // Guard: mapu překreslujeme jen když se výběr reálně liší, jinak zbytečný SELECT event → cyklus.
            if (
                !sameFeatureIdSet(
                    pointers.map(p => p.featureId),
                    selectedDetailFeature?.map(f => f.featureId),
                )
            ) {
                handleMove(pointers);
            }
        } else if (suppressHandleMoveRef.current) {
            suppressHandleMoveRef.current = false;
        } else if ((selectedDetailFeature?.length ?? 0) > 0) {
            // Guard: odvýběr posíláme jen když v mapě reálně něco vybráno je.
            handleMove([]);
        }
    }, [JSON.stringify(attrFinalTable?.checkedIds), isProvazatChecked, selectedLayerId]);

    // Výchozí vybrané položky filtru (jen atribut použitý jako label) nastavíme vždy jen jednou pro danou
    // instanci tabulky. Dřív se to dělo při každé změně dat atributů – ta se přenačítají i při návratu do okna
    // (refetchOnWindowFocus), takže se uživateli „odškrtly" položky filtru, jejichž hodnoty ale ve filtru zůstaly
    // a tabulka podle nich dál filtrovala (MCW-29314).
    const defaultFilterFieldsSetForRef = useRef<string | null>(null);
    useEffect(() => {
        if (!layerAttributesQuery.data || !attrFinalTable) return;
        if (defaultFilterFieldsSetForRef.current === ftKey) return;

        defaultFilterFieldsSetForRef.current = ftKey;
        const labelAttr = layerAttributesQuery.data.find((col: LayerAttributeVO) => col.pouzitJakoLabel);

        attrFinalTable.setFilterVisibleFields(labelAttr ? [gridFieldName(labelAttr.nazev)] : []);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [layerAttributesQuery.data, ftKey]);

    // Při změně extent filtru (zapnutí/vypnutí nebo nové ID ze serveru) znovu načteme data.
    // Přeskočíme první render – tabulka si data načte sama při inicializaci.
    const isExtentInitialRender = useRef(true);
    useEffect(() => {
        if (isExtentInitialRender.current) {
            isExtentInitialRender.current = false;
            return;
        }
        attrFinalTableData.refetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isExtentChecked, extentFeatures]);

    // Po (re)inicializaci tabulky nebo načtení atributů nastav diskriminátorový filtr.
    // Pokud je nastaveno diskriminované dítě, ale atributy ještě nejsou načteny, počkáme.
    // Bez diskriminátoru resetuj field values filtr.
    useEffect(() => {
        if (attributeTableDiscriminatedChildId && !closureDiscAttrNazev) {
            // Atributy se ještě načítají – filtr nastavíme, až budou k dispozici
            return;
        }
        if (closureDiscAttrNazev && closureMatchCiselnikId != null) {
            attrFinalTable.setFieldValuesFilter({
                [gridFieldName(closureDiscAttrNazev)]: {
                    filterType: FieldValuesFilterType.VALUES,
                    values: [String(closureMatchCiselnikId)],
                },
            });
        } else {
            attrFinalTable.setFieldValuesFilter({});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ftKey, closureDiscAttrNazev, closureMatchCiselnikId]);

    // ------------------- Bulk edit / delete -------------------

    const opravneniQuery = useQuery<UserRightsVO[]>(
        [CACHE_KEY_USER_LAYER_OPRAVNENI, selectedLayerId],
        async () => (await RightsApi.getAllLayerRightsForLoggedInUser([Number(selectedLayerId)])).data,
        {enabled: !!selectedLayerId},
    );

    const handleBulkEditClick = useCallback(
        async (checkedIds: number[], akceId: number | null) => {
            const attributes = await Modal.showWaiting(
                actionApiCall(async () => {
                    if (akceId != null) {
                        return (await AkceApi.getActionAttributes(akceId)).data;
                    }
                    return (await UserLayersApi.getLayerAttributes(Number(selectedLayerId), context ?? undefined)).data;
                }),
                ApiCallWaitingModal,
            );

            Modal.show(({onClose}) => (
                <BulkEditModal
                    attributes={attributes ?? []}
                    featureIds={checkedIds}
                    actionId={akceId}
                    onClose={onClose}
                    onSubmit={async (params: BatchUpdateParamsVO, actionId: number | null) => {
                        await actionApiCall(async () => UserLayersApi.updateLayerFeatureBatch(Number(selectedLayerId), actionId ?? undefined, params));
                        attrFinalTableData.invalidateDataRows(checkedIds);
                        await queryClient.invalidateQueries([CACHE_FEATURES_IDS, selectedLayerId]);
                    }}
                />
            ));
        },
        [selectedLayerId, context, attrFinalTableData, queryClient],
    );

    const handleBulkDeleteClick = useCallback(
        (checkedIds: number[]) => {
            const pointers = checkedIds.map(id => ({layerId: selectedLayerId, featureId: id}));
            handleDelete(pointers, async () => {
                attrFinalTableData.invalidateDataRows(checkedIds);
                await queryClient.invalidateQueries([CACHE_FEATURES_IDS, selectedLayerId]);
            });
        },
        [handleDelete, selectedLayerId, attrFinalTableData, queryClient],
    );

    const renderBulkActions = (checkedIds: number[]) => {
        if (!checkedIds.length) return null;
        const opravneni = opravneniQuery.data?.[0]?.layers?.[0];
        const userActions = opravneni?.actionRights?.actionList?.filter(a => a.typ === 'UPDATE') ?? [];
        const isOwnerOrAll = opravneni?.vlastnikVrstvy || opravneni?.actionRights?.allActions;
        const canEdit = isOwnerOrAll || userActions.length > 0;
        const canDelete = isOwnerOrAll;
        if (!canEdit && !canDelete) return null;

        return (
            <Space>
                {userActions.map(action => (
                    <Button key={action.id} style="link" onClick={() => handleEditAction(checkedIds, action.id)}>
                        {action.nazev}
                    </Button>
                ))}
                {isOwnerOrAll && (
                    <Button style="link" onClick={() => handleEditAction(checkedIds, null)}>
                        Editovat objekt
                    </Button>
                )}
                {canDelete && (
                    <Button style="link" onClick={() => handleBulkDeleteClick(checkedIds)}>
                        Smazat vybrané ({checkedIds.length})
                    </Button>
                )}
            </Space>
        );
    };

    // ------------------- Fetch action data for edit dialog -------------------

    const fetchActionData = useCallback(
        async (layerId: LayerId, row: DataGridRowVO, action: UserAction | null) => {
            if (action?.akceId != null) {
                const detailData = await UserLayersApi.getLayerDetail(Number(layerId), String(row.id), action.akceId);
                const actionAttributesData = await AkceApi.getActionAttributes(action.akceId);
                return {detail: detailData.data as LayerDetailVO, actionAttributes: actionAttributesData.data};
            }
            const [detailData, layerAttrsData] = await Promise.all([
                UserLayersApi.getLayerDetail(Number(layerId), String(row.id), undefined, context),
                UserLayersApi.getLayerAttributes(Number(layerId), context),
            ]);
            return {detail: detailData.data as LayerDetailVO, actionAttributes: layerAttrsData.data};
        },
        [context, selectedLayerId],
    );

    const handleUpdateClick = useCallback(
        async (row: DataGridRowVO, action: UserAction | null) => {
            const {detail, actionAttributes} = await Modal.showWaiting(
                actionApiCall(async () => fetchActionData(selectedLayerId, row, action)),
                ApiCallWaitingModal,
            );
            const finalLayerAttributes: LayerAttributeVO[] = actionAttributes && actionAttributes.length > 0 ? actionAttributes : [];
            const finalInitialValues = (detail?.properties ?? row) as Record<string, unknown>;
            const actionId = action?.akceId ?? null;
            handleUpdate(detail ?? row, finalLayerAttributes, finalInitialValues, true, actionId, () => {
                attrFinalTableData.invalidateDataRows([row.id]);
                if (isProvazatChecked) handleMove([{layerId: moveLayerId(row), featureId: row.id}]);
            });
        },
        [handleUpdate, attrFinalTableData, isProvazatChecked, handleMove, selectedLayerId, fetchActionData],
    );

    // Editační akce z lišty hromadných akcí. Při jednom vybraném záznamu spustíme
    // standardní editaci jednoho záznamu, jinak hromadnou editaci přes BulkEditModal.
    const handleEditAction = useCallback(
        async (checkedIds: number[], akceId: number | null) => {
            if (checkedIds.length === 1) {
                const row = getRowById(checkedIds[0]);
                if (row) {
                    await handleUpdateClick(row, akceId != null ? {akceId} : null);
                    return;
                }
            }
            await handleBulkEditClick(checkedIds, akceId);
        },
        [getRowById, handleUpdateClick, handleBulkEditClick],
    );

    const handleDeleteClick = useCallback(
        async (row: DataGridRowVO) => {
            handleDelete([{layerId: selectedLayerId, featureId: row.id}], async () => {
                attrFinalTableData.invalidateDataRows([row.id]);
                await queryClient.invalidateQueries([CACHE_FEATURES_IDS, selectedLayerId]);
            });
        },
        [handleDelete, attrFinalTableData, queryClient, selectedLayerId],
    );

    // Kompatibilní objekt pro AttributeTableTitle, která vyžaduje reset řazení/filtrů při přepnutí vrstvy
    const rowsDataCompat = useMemo(
        () => ({
            rows: {setSort: () => attrFinalTable.setSort([])},
            filter: {setFilter: () => attrFinalTable.setFilter({} as DataGridFilterVO, {} as DataGridFilterVO)},
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [attrFinalTable.setSort, attrFinalTable.setFilter],
    );

    const moveLayerId = useCallback(
        (row: DataGridRowVO) => {
            return resolveMoveLayerId(layersConfigQuery.data, userDbLayers, selectedLayerId, row, discriminatorData.discriminatorAttribute);
        },
        [layersConfigQuery.data, userDbLayers, selectedLayerId, discriminatorData.discriminatorAttribute],
    );

    return (
        <Card
            className="d-flex flex-column att_table_container"
            style={{height: `${tableHeight}px`}}
            title={
                <AttributeTableTitle
                    catalogLayersQuery={catalogLayersQuery}
                    layersConfigData={layersConfigQuery.data}
                    activeLayers={activeLayers}
                    layerId={selectedLayerId}
                    setSelectedLayerId={setSelectedLayerId}
                    rowsData={rowsDataCompat}
                />
            }
            extra={
                <AttributeTableToolbar
                    formats={formats}
                    onExportXlsx={handleExportXlsx}
                    calcQueryIds={(attrFinalTable.ids?.filter(id => typeof id === 'number') ?? extentFeatures ?? []) as FeatureId[]}
                    layerId={selectedLayerId}
                    setIsExtentChecked={setIsExtentChecked}
                    isExtentChecked={isExtentChecked}
                    setIsProvazatChecked={setIsProvazatChecked}
                    isProvazatChecked={isProvazatChecked}
                    discriminatedLayerId={discriminatorData.discriminatorAttribute?.layerId}
                    setIsOpen={setIsOpen}
                    mapRef={mapRef}
                />
            }
        >
            <div className="resize-handle" onMouseDown={handleMouseDown} />
            <Block>
                <FinalTable<DataGridRowVO, DataGridFilterVO, number>
                    className="w-100"
                    finalTableProps={attrFinalTable}
                    highlightId={selectedDetailFeature ? (selectedDetailFeature?.[0]?.featureId as number) : null}
                    extra={({checkedIds}) => renderBulkActions(checkedIds)}
                    getRowProps={({row}) => ({
                        onClick: e => {
                            // Checkbox click se řeší přes checkedIds efekt — zde pouze klik na řádek
                            if (isProvazatChecked) {
                                handleMove([{layerId: moveLayerId(row), featureId: row.id}]);
                            }
                        },
                    })}
                />
            </Block>
        </Card>
    );
};
